import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { TableStore } from 'shared/ui/Table'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import { AutomationLog } from 'entities/Integrations/model/AutomationLog'
import {
  IntegrationsApi,
  type IParamsAutomationLogsList,
  type IResponseAutomationLog,
  type IResponseAutomationLogs,
} from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import { FiltersAndSearchStore } from 'widgets/FiltersAndSearch'
import { AutomationLogModalDetailsContent } from '../pages/AutomationLogs/ui/AutomationLogModalDetailsContent/AutomationLogModalDetailsContent'

export class AutomationLogsListStore {
  private _disposeReactionSearchApplied: IReactionDisposer | null = null
  private _disposeReactionSortingApplied: IReactionDisposer | null = null
  private _disposeReactionFiltersApplied: IReactionDisposer | null = null

  filtersAndSearchStore
  tableStore
  _disposeParamsGetItems
  _disposeParamsWithoutPage

  itemsMap = new Map<number, AutomationLog>()
  firstLoading = true
  startPaginationSize = 10
  limit = tableLimitStore.getLimit('automation-logs-table') ?? 10
  private _hasRetriedAtMinLimit = false
  page = 1
  total = 0

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this.filtersAndSearchStore = new FiltersAndSearchStore(
      {
        getFilters: () => IntegrationsApi.getAutomationLogsFilters().then(({ data }) => data),
      },
      this._entities
    )
    this.tableStore = new TableStore<AutomationLog>({
      getColumns: this._getColumns(),
    })

    this.init()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this.page = 1
      }
    )

    this.reactionSearchApplied()
    this.reactionSortingApplied()
    this.reactionFiltersApplied()
  }

  dispose = () => {
    this._disposeParamsGetItems()
    this._disposeParamsWithoutPage()
    this._disposeReactionSearchApplied?.()
    this._disposeReactionSortingApplied?.()
    this._disposeReactionFiltersApplied?.()
    this._requestLoadData.dispose()
  }

  get paramsWithoutPage(): Omit<IParamsAutomationLogsList, 'page'> {
    return {
      limit: this.limit,
      search: this.filtersAndSearchStore.search,
      filters: this.filtersAndSearchStore.noEmptyFilters,
      sortBy: this.tableStore.sortBy || 'executed_at',
      sortOrder: this.tableStore.sortOrder,
    }
  }

  get loading() {
    return this._requestLoadData.loading
  }

  get paramsGetItems(): IParamsAutomationLogsList {
    return {
      ...this.paramsWithoutPage,
      page: this.page,
    }
  }

  get hasItems() {
    return !!this.items.length
  }

  get items() {
    return Array.from(this.itemsMap.values())
  }

  setItem = (response: IResponseAutomationLog) => {
    this.itemsMap.set(response.id, new AutomationLog(response))
  }

  setItems = (items: Array<IResponseAutomationLog>) => {
    items.forEach((item) => {
      this.setItem(item)
    })
  }

  setLoading = (value: boolean) => {
    this._requestLoadData.setLoading(value)
    this.firstLoading = false
  }

  init = () => {
    this.loadData()
  }

  onPaginationModelChange = (page: number, limit: number) => {
    this.page = page
    this.limit = limit
    tableLimitStore.setLimit('automation-logs-table', limit)
  }

  get isEmpty() {
    return !this.total && !this.filtersAndSearchStore.hasSearchParams
  }

  private _requestLoadData = new RequestLoadData<boolean>({
    requestCallback: async ({ cancelRequest }, noLoading) => {
      runInAction(() => {
        if (noLoading) {
          this._requestLoadData.setLoading(false)
        }
      })

      try {
        const { data } = await IntegrationsApi.getAutomationLogs(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })

        runInAction(() => {
          this._hasRetriedAtMinLimit = false
        })
        this._setData(data)
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            'TABLE: automation-logs-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const currentLimit = this.limit
          const newLimit = tableLimitStore.reduceLimit('automation-logs-table', currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
            })
            this.limit = newLimit
            tableLimitStore.setLimit('automation-logs-table', newLimit)
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
            })
            this._requestLoadData.debounceLoadData()
          }
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      this.setLoading(false)
    },
  })

  loadData = async (noLoading?: boolean) => {
    return this._requestLoadData.loadData(noLoading)
  }

  private _setData = ({ data, meta }: IResponseAutomationLogs) => {
    this.itemsMap.clear()
    this.setItems(data)
    this.total = meta.total
  }

  private _getColumns = async () => {
    return [
      'source',
      'entity_id',
      'callback_id',
      'step',
      'url',
      'action',
      'status',
      'result',
      'contact_name',
      'contact_phone',
      'executed_at',
    ]
  }

  logEvent = (action: 'Search applied' | 'Filter applied' | 'Sorting applied') => {
    this._entities.eventLogStore.logEvent(
      'Automation Logs Used',
      {
        event_id: 'automation_logs_used',
        action,
        agencyId: this._entities.organizationStore.agency_id,
        userId: this._entities.usersStore.user_owner_id,
      },
      { groupId: this._entities.organizationStore.id }
    )
  }

  onDetails = (item: AutomationLog) => {
    modalStore.addModal({
      id: nanoid(),
      width: 540,
      position: 'right',
      type: ModalTypeList.DEFAULT,
      ModalContentProps: { item },
      ModalContent: AutomationLogModalDetailsContent,
      title: 'Details',
      pureContent: true,
      fullHeight: true,
    })
  }

  private _sendLogEvent = new RequestLoadData<
    'Search applied' | 'Filter applied' | 'Sorting applied'
  >({
    requestCallback: async (config, event) => {
      if (event) this.logEvent(event)
    },
  })

  reactionSearchApplied = () => {
    this._disposeReactionSearchApplied?.()
    this._disposeReactionSearchApplied = reaction(
      () => this.filtersAndSearchStore.search,
      (value) => {
        if (value) this._sendLogEvent.debounceLoadData('Search applied')
      }
    )
  }

  reactionSortingApplied = () => {
    this._disposeReactionSortingApplied?.()
    this._disposeReactionSortingApplied = reaction(
      () => [this.tableStore.sortBy, this.tableStore.sortOrder],
      () => {
        this._sendLogEvent.loadData('Sorting applied')
      }
    )
  }

  reactionFiltersApplied = () => {
    this._disposeReactionFiltersApplied?.()
    this._disposeReactionFiltersApplied = reaction(
      () => [this.filtersAndSearchStore.noEmptyFilters],
      () => {
        this._sendLogEvent.debounceLoadData('Filter applied')
      }
    )
  }
}
